import { types, flow } from "mobx-state-tree";
import { UserModel, User } from "../users/user";
import { PermissionsUserModel } from "../permissions/models";
import { UsersApi } from "../../../api/users/UsersApi";
import normalize from "jsonapi-normalizer";
import {
  PasswordTokenRequest,
  GrantType,
  AuthApi,
  RefreshTokenRequest,
  OauthTokenResponse,
} from "../../../api/users/AuthApi";
import { setRefreshToken, setAccessToken, clearTokens } from "../../../helpers/cookies";

export interface LoginOptions {
  username: string;
  password: string;
  rememberMe: boolean;
}

export const AuthModel = types
  .model({
    authenticatedUser: types.maybeNull(UserModel),
    isLogged: types.boolean,
    isLoading: types.boolean,
    permissions: types.maybeNull(PermissionsUserModel),
  })
  .actions((self) => {
    const me = flow(function* () {
      try {
        const response = yield UsersApi.me({
          include: ["profiles", "permissions"],
        });
        const { users, permissions } = normalize(response).entities;
        self.permissions = Object.values(permissions) as any;
        self.authenticatedUser = Object.values(users)[0] as User;
      } catch (e) {
      } finally {
      }
    });

    const login = flow(function* (data: LoginOptions) {
      try {
        self.isLoading = true;
        const { rememberMe, username, password } = data;
        const loginData: PasswordTokenRequest = {
          client_id: process.env.REACT_APP_CLIENT_ID!,
          client_secret: process.env.REACT_APP_CLIENT_SECRET!,
          username,
          password,
          grant_type: GrantType.password,
          scope:
              "users.users.own.read " +
              "users.users.own.update " +
              "users.users.own.delete " +
              "users.users.any.create " +
              "users.users.any.read " +
              "users.users.any.update " +
              "users.users.any.delete " +
              "users.users_profiles_roles_permissions.any.create " +
              "users.users_profiles_roles_permissions.any.read " +
              "users.users_profiles_roles_permissions.any.delete " +
              "questions.skills.any.create " +
              "questions.skills.any.read " +
              "questions.skills.any.update " +
              "questions.skills.any.delete " +
              "questions.topics.any.create " +
              "questions.topics.any.read " +
              "questions.topics.any.update " +
              "questions.topics.any.delete " +
              "questions.questions.any.create " +
              "questions.questions.any.read " +
              "questions.questions.any.update " +
              "questions.questions.any.delete"
        };
        const response = yield AuthApi.login(loginData);
        const { access_token, expires_in, refresh_token } = response;
        setRefreshToken(refresh_token, { path: "/" });
        setAccessToken(access_token, {
          expires_in,
        });
        self.isLogged = true;
        localStorage.setItem("rememberMe", JSON.stringify(rememberMe));
        if (rememberMe) {
          localStorage.setItem("username", username);
        } else {
          localStorage.removeItem("username");
        }
      } catch (e) {
      } finally {
        self.isLoading = false;
      }
    });

    const logout = function () {
      clearTokens();
      self.isLogged = false;
    };

    const refreshToken = flow(function* (token: string) {
      try {
        self.isLoading = true;
        const refreshTokenData: RefreshTokenRequest = {
          client_id: process.env.REACT_APP_CLIENT_ID!,
          client_secret: process.env.REACT_APP_CLIENT_SECRET!,
          refresh_token: token,
          grant_type: GrantType.token,
        };
        const response: OauthTokenResponse = yield AuthApi.token(refreshTokenData);
        const { access_token, expires_in, refresh_token } = response;
        setRefreshToken(refresh_token, { path: "/" });
        setAccessToken(access_token, {
          expires_in,
        });
        self.isLogged = true;
      } catch (e) {
        self.isLogged = false;
      } finally {
        self.isLoading = false;
      }
    });

    const setPermissions = function (permissions: any) {
      self.permissions = permissions;
    };

    return {
      me,
      login,
      logout,
      refreshToken,
      setPermissions,
    };
  })
  .views((self) => ({
    get authenticatedUserId() {
      if (self.authenticatedUser == null) return "";
      return self.authenticatedUser.id;
    },

    get permissionsList() {
      return Object.values(self.permissions || []).map(permission => permission.title);
    }
  }));
